// Publieke tevredenheidspagina (/tevredenheid, mailronde 06-08).
//
// De klant komt hier via de tokenlink onder de handtekening van een
// verzonden antwoordmail. Geen inlog: het token is de autorisatie, met
// als scope precies één meting. Opzet en palet volgen de andere
// publieke shop-pagina's (retour/retourstatus): oranje
// brand-orange-600, koppen gray-900, tekst #333333, vlak gray-50,
// groen #2E9E5B, shopfont via .retour-shop.
//
// Bewust licht gehouden: één vraag (score 1-5), optioneel een korte
// toelichting, klaar. Een ongeldige of verlopen link krijgt één
// generieke melding — geen onderscheid tussen verlopen en vervalst.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";

import "@fontsource/nunito-sans/400.css";
import "@fontsource/nunito-sans/600.css";
import "@fontsource/nunito-sans/700.css";
import "@fontsource/arimo/700.css";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { mailCsatApi } from "@/lib/api";
import {
  RetourFooter,
  RetourHeader,
  RETOUR_KOLOM,
} from "../Retour/RetourShopElementen";

const GROEN = "#2E9E5B";

type Weergave = "laden" | "vraag" | "bedankt" | "ongeldig";

const SCORES = [1, 2, 3, 4, 5] as const;

export function TevredenheidPagina() {
  const { t } = useTranslation();
  const [zoekParams] = useSearchParams();
  const token = zoekParams.get("token") ?? "";

  const [weergave, setWeergave] = useState<Weergave>(() =>
    token ? "laden" : "ongeldig",
  );
  const [score, setScore] = useState<number | null>(null);
  const [toelichting, setToelichting] = useState("");
  const [bezig, setBezig] = useState(false);
  const [alEerderIngevuld, setAlEerderIngevuld] = useState(false);

  useEffect(() => {
    if (!token) return;
    let actief = true;
    mailCsatApi
      .context(token)
      .then((ctx) => {
        if (!actief) return;
        setAlEerderIngevuld(ctx.al_ingevuld);
        if (ctx.score != null) setScore(ctx.score);
        setWeergave("vraag");
      })
      .catch(() => {
        if (actief) setWeergave("ongeldig");
      });
    return () => {
      actief = false;
    };
  }, [token]);

  const verstuur = (gekozenScore: number) => {
    if (bezig) return;
    setBezig(true);
    mailCsatApi
      .stuurScore(token, gekozenScore, toelichting.trim() || undefined)
      .then(() => setWeergave("bedankt"))
      .catch(() => setWeergave("ongeldig"))
      .finally(() => setBezig(false));
  };

  return (
    <div className="retour-shop flex min-h-screen flex-col bg-white">
      <RetourHeader />
      <main className="flex-1 bg-gray-50 py-10">
        <div className={`${RETOUR_KOLOM} max-w-[640px]`}>
          {weergave === "laden" && (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}

          {weergave === "ongeldig" && (
            <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
              <h1 className="text-[22px] font-bold text-gray-900">
                {t("tevredenheid.ongeldig_titel")}
              </h1>
              <p className="mt-2 text-[14px] leading-relaxed text-[#333333]">
                {t("tevredenheid.ongeldig_uitleg")}
              </p>
            </div>
          )}

          {weergave === "bedankt" && (
            <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
              <span
                className="mx-auto grid h-12 w-12 place-items-center rounded-full"
                style={{ background: "#e8f5ee" }}
              >
                <Check className="h-6 w-6" style={{ color: GROEN }} />
              </span>
              <h1 className="mt-4 text-[22px] font-bold text-gray-900">
                {t("tevredenheid.bedankt_titel")}
              </h1>
              <p className="mt-2 text-[14px] leading-relaxed text-[#333333]">
                {t("tevredenheid.bedankt_uitleg")}
              </p>
            </div>
          )}

          {weergave === "vraag" && (
            <div className="rounded-xl border border-gray-200 bg-white p-8">
              <h1 className="text-[22px] font-bold text-gray-900">
                {t("tevredenheid.vraag_titel")}
              </h1>
              <p className="mt-2 text-[14px] leading-relaxed text-[#333333]">
                {alEerderIngevuld
                  ? t("tevredenheid.al_ingevuld_uitleg")
                  : t("tevredenheid.vraag_uitleg")}
              </p>

              <div
                className="mt-6 flex items-center justify-between gap-2"
                role="radiogroup"
                aria-label={t("tevredenheid.vraag_titel")}
              >
                {SCORES.map((waarde) => (
                  <button
                    key={waarde}
                    type="button"
                    role="radio"
                    aria-checked={score === waarde}
                    data-testid={`score-${waarde}`}
                    onClick={() => setScore(waarde)}
                    className="flex h-14 flex-1 flex-col items-center justify-center rounded-lg border text-[17px] font-bold transition-colors"
                    style={
                      score === waarde
                        ? {
                            borderColor: "#ef7f01",
                            background: "#fff4e8",
                            color: "#d77200",
                          }
                        : {
                            borderColor: "#e5e7eb",
                            background: "#ffffff",
                            color: "#6b7280",
                          }
                    }
                  >
                    {waarde}
                  </button>
                ))}
              </div>
              <div className="mt-1.5 flex justify-between text-[11.5px] text-gray-400">
                <span>{t("tevredenheid.schaal_laag")}</span>
                <span>{t("tevredenheid.schaal_hoog")}</span>
              </div>

              <label className="mt-6 block">
                <span className="text-[13px] font-semibold text-gray-900">
                  {t("tevredenheid.toelichting_label")}
                </span>
                <Textarea
                  value={toelichting}
                  onChange={(e) => setToelichting(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  placeholder={t("tevredenheid.toelichting_placeholder")}
                  className="mt-1.5 bg-white"
                />
              </label>

              <Button
                type="button"
                disabled={score === null || bezig}
                onClick={() => score !== null && verstuur(score)}
                className="mt-5 h-11 w-full text-[15px]"
              >
                {bezig ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t("tevredenheid.versturen")
                )}
              </Button>
            </div>
          )}
        </div>
      </main>
      <RetourFooter />
    </div>
  );
}
